/**
 * D4 — the per-column report, arranged for the adoption review screen.
 *
 * PURE, and deliberately outside the component: `web/` has no component test
 * runner, so anything with a rule in it lives here and is tested through
 * `functions`' vitest (`npm run test:web`).
 *
 * THE RULE: no column of the uploaded file is left off the screen. Jim adopted
 * a 1,591-row dictionary on 2026-09-15 and found help text, required flags,
 * ranges, branching and his own five columns had reached nothing, with the
 * screen saying nothing at all. Columns are therefore GROUPED, never filtered.
 */

/** Mirrors `functions/src/dataDictionary/adoptColumnReport.ts`. */
export interface ColumnReportRow {
  column: string;
  sponsorColumn: boolean;
  valuesInFile: number;
  storedOnEntry: boolean;
  reachesForm: boolean;
  changesProposed: number;
  reason?: string;
}

export type ColumnReportGroupKey = "form" | "dictionary" | "dropped";

export interface ColumnReportGroup {
  key: ColumnReportGroupKey;
  /** Plain language, no internal column names. */
  heading: string;
  rows: ColumnReportRow[];
}

const HEADINGS: Record<ColumnReportGroupKey, string> = {
  form: "Applied to your forms",
  dictionary: "Kept in the study's dictionary (the forms have nowhere to show these)",
  dropped: "Not kept",
};

/** Which group a column belongs to. Total: every column matches exactly one. */
function groupKeyOf(row: ColumnReportRow): ColumnReportGroupKey {
  if (!row.storedOnEntry) return "dropped";
  return row.reachesForm ? "form" : "dictionary";
}

/**
 * The report split into groups, in the file's own column order within each.
 * A group with no columns is omitted rather than drawn as an empty heading.
 */
export function groupColumnReport(rows: ColumnReportRow[]): ColumnReportGroup[] {
  const order: ColumnReportGroupKey[] = ["form", "dictionary", "dropped"];
  return order
    .map((key) => ({ key, heading: HEADINGS[key], rows: rows.filter((r) => groupKeyOf(r) === key) }))
    .filter((g) => g.rows.length > 0);
}

/** Columns that hold values the forms will not carry. The number that matters. */
function columnsWithUnusedValues(rows: ColumnReportRow[]): ColumnReportRow[] {
  return rows.filter((r) => r.valuesInFile > 0 && !r.reachesForm);
}

/** One sentence above the table. */
export function columnReportSummary(rows: ColumnReportRow[]): string {
  const unused = columnsWithUnusedValues(rows);
  if (unused.length === 0) {
    return "Every column in your file that has values is applied to your forms.";
  }
  const values = unused.reduce((n, r) => n + r.valuesInFile, 0);
  const c = unused.length === 1 ? "column" : "columns";
  const v = values === 1 ? "value" : "values";
  return (
    `${unused.length} ${c} in your file hold ${values} ${v} your forms cannot show yet. ` +
    `They are listed below — nothing is dropped without being named.`
  );
}

/**
 * Open the table by default when there is something the user would not expect:
 * values that do not reach a form, or a column that cannot be kept at all.
 */
export function shouldOpenColumnReport(rows: ColumnReportRow[]): boolean {
  if (columnsWithUnusedValues(rows).length > 0) return true;
  return rows.some((r) => !r.storedOnEntry && r.valuesInFile > 0);
}

/** "1,591 values" / "no values" — for the count cell. */
export function formatValueCount(n: number): string {
  if (n === 0) return "no values";
  return `${n.toLocaleString()} ${n === 1 ? "value" : "values"}`;
}
